import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { PromotionService } from '../services/promotion.service';
import * as PromotionActions from './promotions.actions';

@Injectable()
export class PromotionsEffects {
    private _actions$ = inject(Actions);
    private _promotionService = inject(PromotionService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all promotions',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Promotions
    getAllPromotions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PromotionActions.loadPromotions),
            switchMap(() => {
                return this._promotionService.getAll().pipe(
                    map((res) => {
                        return PromotionActions.loadPromotionsSuccess({
                            promotions: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            PromotionActions.loadPromotionsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Promotion
    getPromotion$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PromotionActions.loadPromotion),
            switchMap((action) => {
                return this._promotionService.getOne(action.id).pipe(
                    map((res) => {
                        return PromotionActions.loadPromotionSuccess({
                            promotion: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            PromotionActions.loadPromotionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Promotion
    createPromotion$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PromotionActions.createPromotion),
            switchMap((action) => {
                return this._promotionService.create(action.data).pipe(
                    map((res) => {
                        return PromotionActions.createPromotionSuccess({
                            promotion: res.data,
                            success: 'Promotion created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            PromotionActions.createPromotionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Promotion
    updatePromotion$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PromotionActions.updatePromotion),
            switchMap((action) => {
                return this._promotionService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            return PromotionActions.updatePromotionSuccess({
                                promotion: res.data,
                                success: 'Promotion updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                PromotionActions.updatePromotionFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Promotion
    deletePromotion$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PromotionActions.deletePromotion),
            switchMap((action) => {
                return this._promotionService.delete(action.id).pipe(
                    map(() => {
                        return PromotionActions.deletePromotionSuccess({
                            id: action.id,
                            success: 'Promotion deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            PromotionActions.deletePromotionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onPromotionActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    PromotionActions.createPromotionSuccess,
                    PromotionActions.updatePromotionSuccess,
                    PromotionActions.deletePromotionSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onPromotionActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    PromotionActions.loadPromotionsFailure,
                    PromotionActions.loadPromotionFailure,
                    PromotionActions.createPromotionFailure,
                    PromotionActions.updatePromotionFailure,
                    PromotionActions.deletePromotionFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
